// Activity Types - Comprehensive categorization of all possible activities
export const ACTIVITY_TYPES = {
  USER_ACTION: "user_action",
  SYSTEM_EVENT: "system_event",
  GENIE_INTERACTION: "genie_interaction",
  CONVERSATION: "conversation",
  DOCUMENT: "document",
  AUTHENTICATION: "authentication",
  ADMIN: "admin",
} as const;

// Activity Actions - Specific actions that can be performed
export const ACTIVITY_ACTIONS = {
  // General CRUD
  CREATED: "created",
  UPDATED: "updated",
  DELETED: "deleted",
  VIEWED: "viewed",

  // Authentication
  LOGGED_IN: "logged_in",
  LOGGED_OUT: "logged_out",
  PASSWORD_CHANGED: "password_changed",
  PROFILE_UPDATED: "profile_updated",

  // Genie Management
  GENIE_CREATED: "genie_created",
  GENIE_UPDATED: "genie_updated",
  GENIE_DELETED: "genie_deleted",
  GENIE_LAUNCHED: "genie_launched",
  GENIE_CONFIGURED: "genie_configured",
  GENIE_CATEGORY_CHANGED: "genie_category_changed",

  // Conversations
  CONVERSATION_STARTED: "conversation_started",
  CONVERSATION_ENDED: "conversation_ended",
  CONVERSATION_ANALYZED: "conversation_analyzed",
  CONVERSATION_EXPORTED: "conversation_exported",

  // Documents
  DOCUMENT_UPLOADED: "document_uploaded",
  DOCUMENT_PROCESSED: "document_processed",
  DOCUMENT_SHARED: "document_shared",

  // System Events
  DEPLOYMENT: "deployment",
  MAINTENANCE: "maintenance",
  ERROR_OCCURRED: "error_occurred",
  BACKUP_COMPLETED: "backup_completed",

  // Settings & Configuration
  SETTINGS_UPDATED: "settings_updated",
  EMAIL_CONFIGURED: "email_configured",
  PRONUNCIATION_ADDED: "pronunciation_added",
  QR_URL_CHANGED: "qr_url_changed",

  // Engagement
  PAGE_VISITED: "page_visited",
  FEATURE_USED: "feature_used",
  TUTORIAL_COMPLETED: "tutorial_completed",

  // Team Management
  TEAM_MEMBER_INVITED: "team_member_invited",
  TEAM_CREATED: "team_created",
  TEAM_UPDATED: "team_updated",

  // Branding
  BRANDING_UPDATED: "branding_updated",
} as const;

// Resource Types - Types of resources that can be acted upon
export const RESOURCE_TYPES = {
  GENIE: "genie",
  CONVERSATION: "conversation",
  DOCUMENT: "document",
  USER: "user",
  USER_PROFILE: "user_profile",
  USER_ACCOUNT: "user_account",
  SYSTEM: "system",
  PAGE: "page",
  FEATURE: "feature",
  SETTINGS: "settings",
  TEAM: "team",
  TEAM_MANAGEMENT: "team_management",
} as const;

// Activity Categories - For organizing activities in the UI
export const ACTIVITY_CATEGORIES = {
  GENIE: "genie",
  CONVERSATION: "conversation",
  DOCUMENT: "document",
  ACCOUNT: "account",
  USER_MANAGEMENT: "user_management",
  SECURITY: "security",
  SYSTEM: "system",
  GENERAL: "general",
  SETTINGS: "settings",
} as const;

// Activity Priority Levels
export const ACTIVITY_PRIORITIES = {
  LOW: "low",
  NORMAL: "normal",
  HIGH: "high",
  CRITICAL: "critical",
} as const;

// Activity Status
export const ACTIVITY_STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
} as const;

// Type definitions
export type ActivityType = (typeof ACTIVITY_TYPES)[keyof typeof ACTIVITY_TYPES];
export type ActivityAction = (typeof ACTIVITY_ACTIONS)[keyof typeof ACTIVITY_ACTIONS];
export type ResourceType = (typeof RESOURCE_TYPES)[keyof typeof RESOURCE_TYPES];
export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[keyof typeof ACTIVITY_CATEGORIES];
export type ActivityPriority = (typeof ACTIVITY_PRIORITIES)[keyof typeof ACTIVITY_PRIORITIES];
export type ActivityStatus = (typeof ACTIVITY_STATUS)[keyof typeof ACTIVITY_STATUS];

export interface Activity {
  id: string;
  created_at: string;
  user_id?: string;
  session_id?: string;
  activity_type: ActivityType;
  action: ActivityAction;
  resource_type?: ResourceType;
  resource_id?: string;
  resource_name?: string;
  title: string;
  description?: string;
  metadata?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  referrer?: string;
  is_visible: boolean;
  priority: ActivityPriority;
  category: ActivityCategory;
  duration_ms?: number;
  status: ActivityStatus;
  user_full_name?: string;
  user_avatar_url?: string;
}

export interface GetActivitiesApiParams {
  limit?: number;
  offset?: number;
  category?: ActivityCategory | ActivityCategory[];
  activityType?: ActivityType;
  action?: string;
  excludeActions?: string[];
  startDate?: string;
  endDate?: string;
  resourceType?: ResourceType;
  resourceId?: string;
  searchTerm?: string;
}

export interface GetActivitiesResponse {
  activities: Activity[];
  count: number;
  limit: number;
  offset: number;
}

export interface AgentBrandingSummary {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  customAvatarUrl?: string | null;
  gradientEnabled?: boolean;
  gradientAngle?: number;
}

export interface InsightCountWithDelta {
  current: number;
  previous: number;
  delta: number | null;
}

export interface InsightAttentionBreakdown {
  current: number;
  escalations: number;
  lowSentiment: number;
  negativeFeedback: number;
}

export interface InsightSilentBreakdown {
  current: number;
  total: number;
}

export interface InsightsWindow {
  start: string;
  end: string;
}

export interface WeeklySummary {
  conversations: InsightCountWithDelta;
  /** @deprecated Leads are being retired — still returned by the API but no longer shown in the UI. */
  leads: InsightCountWithDelta;
  /**
   * Conversations that arrived outside business hours (8am–6pm weekdays) in the
   * account owner's timezone. `current` is null when the timezone is unknown;
   * the field is absent entirely until the api function is redeployed.
   */
  afterHours?: { current: number | null };
  attention: InsightAttentionBreakdown;
  silentGenies: InsightSilentBreakdown;
  window: InsightsWindow;
}

export interface AttentionEscalation {
  id: string;
  conversationId: string | null;
  agentId: string;
  agentName: string;
  agentBranding: AgentBrandingSummary | null;
  summary: string | null;
  occurredAt: string | null;
  reason: string | null;
}

export interface AttentionLowSentiment {
  id: string;
  conversationId: string | null;
  agentId: string;
  agentName: string;
  agentBranding: AgentBrandingSummary | null;
  summary: string | null;
  score: number | null;
  occurredAt: string | null;
}

export interface AttentionNegativeFeedback {
  id: string;
  conversationId: string | null;
  agentId: string;
  agentName: string;
  agentBranding: AgentBrandingSummary | null;
  feedbackText: string | null;
  occurredAt: string | null;
}

export interface AttentionSilentGenie {
  agentId: string;
  agentName: string;
  agentBranding: AgentBrandingSummary | null;
  createdAt: string | null;
}

export interface NeedsAttention {
  escalations: AttentionEscalation[];
  lowSentiment: AttentionLowSentiment[];
  negativeFeedback: AttentionNegativeFeedback[];
  silentGenies: AttentionSilentGenie[];
  threshold: number;
  window: InsightsWindow;
}

export type AttentionBucket = "escalations" | "lowSentiment" | "negativeFeedback" | "silentGenies";

// ── Conversational Insights digest (AI-synthesized "story this period") ──────

/** A single conversation the digest cites as evidence for a highlight. */
export interface DigestExample {
  /** Full-page conversation id used for routing to /genie-hub/conversations/:id. */
  id: string | null;
  /** Short human label, e.g. "Power BI · pricing question". */
  label: string;
}

export type DigestHighlightKind = "win" | "watch" | "opportunity" | "theme";

/** One synthesized, decision-shaped finding — not a raw extracted line. */
export interface DigestHighlight {
  kind: DigestHighlightKind;
  title: string;
  detail: string;
  /** How many conversations this pattern spans, when meaningful. */
  count?: number;
  examples?: DigestExample[];
}

/** One issue category and how many conversations in the batch fell into it. */
export interface DigestCategoryCount {
  category: string;
  count: number;
}

/** Resolved/partial/unresolved counts across the batch, plus the derived rate. */
export interface DigestResolutionStats {
  resolved: number;
  partial: number;
  unresolved: number;
  /** resolved + partial + unresolved — the denominator for resolutionRate. */
  classified: number;
  /** resolved / classified as a whole percentage, or null when nothing is classified yet. */
  resolutionRate: number | null;
}

/** Issue-category and resolution-rate stats for the digest's batch — backfilled live for conversations missing them, merged with anything already stored per-conversation. */
export interface DigestClassificationStats {
  categoryBreakdown: DigestCategoryCount[];
  resolution: DigestResolutionStats;
  /** How many conversations in the batch were classified live by this digest call (vs. already had it stored). */
  classifiedLive: number;
}

export interface ConversationDigest {
  /** Genie-voiced narrative of what happened this period (2–4 sentences). */
  story: string;
  highlights: DigestHighlight[];
  /** Number of analyzed conversations the digest was synthesized from. */
  basedOn: number;
  generatedAt: string;
  /** False when there wasn't enough analyzed data to synthesize a real story. */
  hasEnoughData: boolean;
  classificationStats?: DigestClassificationStats;
}
